import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { CalendarIcon, PlusIcon } from "../../../shared/components/icons";
import { calendarScreenProps, useCalendarKeys } from "../hooks/useCalendarKeys";
import { useChangeQueue } from "../hooks/useChangeQueue";
import { useConflictAcks } from "../hooks/useConflictAcks";
import { useNow } from "../hooks/useNow";
import { useSelf } from "../hooks/useSelf";
import { useWeekEvents } from "../hooks/useWeekEvents";
import { useWorkingHours } from "../hooks/useWorkingHours";
import {
  allDayEvents,
  bookedSummary,
  displaySubject,
  findClashes,
  timedSegments,
  type Clash,
  type DaySegment,
} from "../domain/day";
import { respondChange, type CalendarChange } from "../domain/changes";
import { canWriteCalendar } from "../domain/permissions";
import type { CalEvent } from "../types";
import { addDays, dayKeyOf, dayName, minutesOfDay, startOfWeekMonday, weekLabel, weekOfLabel } from "../domain/time";
import { fetchCalendarView } from "../services/calendarView";
import { fetchAttachments, fetchRecurrenceLabel } from "../services/eventDetail";
import { findMeetingThread } from "../services/meetingThread";
import { fetchSchedules } from "../services/schedules";
import { ChevronLeftSmIcon, ChevronRightSmIcon } from "./CalendarIcons";
import { CalendarToast } from "./CalendarToast";
import { DayView, type ScheduleMode } from "./DayView";
import { MeetingDetail } from "./MeetingDetail";
import { NewMeetingView } from "./NewMeetingView";
import { RescheduleView } from "./RescheduleView";
import { WeekView } from "./WeekView";
import styles from "./CalendarModule.module.css";

type Screen =
  | { readonly kind: "agenda" }
  /** `preset`: the free time clicked in the day view; step 2 opens straight on the time finder. */
  | { readonly kind: "new"; readonly preset: Date | null; readonly step: 1 | 2 }
  | { readonly kind: "meeting"; readonly eventId: string }
  | { readonly kind: "reschedule"; readonly eventId: string };

type View = "day" | "week";

/** After a new meeting is opened in Outlook, re-read the calendar on return at most this often. */
const RETURN_REFRESH_MS = 15_000;
/** How long a meeting just moved or added stays highlighted: its pulse plays twice (see .flash). */
const FLASH_MS = 1900;

/** The meeting `eventId` clashes with, or null. */
function clashPartner(clashes: readonly Clash[], eventId: string): CalEvent | null {
  const clash = clashes.find((c) => c.a.id === eventId || c.b.id === eventId);
  if (!clash) return null;
  return clash.a.id === eventId ? clash.b : clash.a;
}

/** The day view's header line: how booked the day is, once it has loaded. */
function daySubtitle(segs: readonly DaySegment[], loading: boolean, error: string | null, day: string, today: string): string {
  if (loading) return "Loading…";
  if (error) return dayName(day, today);
  return bookedSummary(segs);
}

interface CalendarModuleProps {
  readonly graphToken: string | null;
  readonly onBack?: () => void;
}

export function CalendarModule({ graphToken, onBack }: Readonly<CalendarModuleProps>) {
  const now = useNow();
  const today = dayKeyOf(now);
  const [day, setDay] = useState(today);
  const [view, setView] = useState<View>("day");
  const [mode, setMode] = useState<ScheduleMode>("timeline");
  const [screen, setScreen] = useState<Screen>({ kind: "agenda" });
  // One fetch covers the selected day's whole week, so the day and week views
  // share it and moving between days in the same week needs no reload.
  const mon = startOfWeekMonday(day);
  const { events: serverEvents, loading, error, truncated, reload, refresh } = useWeekEvents(graphToken, mon);
  // Changes waiting in (or just past) their Undo window show on top of what Graph returned.
  const changes = useChangeQueue(graphToken, refresh);
  const { applyTo } = changes;
  const events = useMemo(() => applyTo(serverEvents), [applyTo, serverEvents]);
  const { acks, acknowledge, forget } = useConflictAcks();
  const workingHours = useWorkingHours(graphToken);
  const { refreshGraphToken } = useAuthState();
  const writable = canWriteCalendar(graphToken);
  const segs = useMemo(() => timedSegments(events, day), [events, day]);
  // The day's meetings in order (all-day first), for the detail screen's pager.
  const dayEvents = useMemo(() => [...allDayEvents(events, day), ...segs.map((s) => s.event)], [events, day, segs]);
  const self = useSelf(graphToken);

  const showAgenda = useCallback(() => setScreen({ kind: "agenda" }), []);
  const showNewMeeting = useCallback(() => setScreen({ kind: "new", preset: null, step: 1 }), []);
  const bookAt = useCallback((start: Date | null) => setScreen({ kind: "new", preset: start, step: 1 }), []);
  const findATime = useCallback(() => setScreen({ kind: "new", preset: null, step: 2 }), []);
  // Opening a meeting moves to its day, so the pager and Back use that day.
  const openEvent = useCallback(
    (eventId: string) => {
      const target = events.find((e) => e.id === eventId);
      if (target) setDay(dayKeyOf(target.start));
      setScreen({ kind: "meeting", eventId });
    },
    [events],
  );
  // See Options on a meeting: back to its day with that meeting's clash and its Resolve options open.
  const [resolveFor, setResolveFor] = useState<string | null>(null);
  const seeClashOptions = useCallback((eventId: string) => {
    setView("day");
    setScreen({ kind: "agenda" });
    setResolveFor(eventId);
  }, []);
  const resolveOpened = useCallback(() => setResolveFor(null), []);
  const goToday = useCallback(() => {
    setDay(today);
    setView("day");
  }, [today]);
  const openDay = useCallback((target: string) => {
    setDay(target);
    setView("day");
  }, []);

  const { flush, notify } = changes;
  // "Leave It" is stored on this device only, so its Undo just forgets the choice.
  const leaveIt = useCallback(
    (key: string, signature: string) => {
      flush();
      acknowledge(key, signature);
      notify({
        kind: "undo",
        message: "Left as is · both stay booked",
        onUndo: () => {
          forget(key);
          notify({ kind: "info", message: "Change undone · nothing was sent" });
        },
      });
    },
    [flush, acknowledge, forget, notify],
  );
  // F4: a meeting just moved or added is highlighted for FLASH_MS, or until its change is undone or fails.
  const { stage: stageChange, isApplied } = changes;
  const [flash, setFlash] = useState<{ readonly eventId: string; readonly changeId: number } | null>(null);
  const stage = useCallback(
    (change: CalendarChange) => {
      stageChange(change);
      if (change.highlightId) setFlash({ eventId: change.highlightId, changeId: change.id });
    },
    [stageChange],
  );
  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(id);
  }, [flash]);
  const flashId = flash && isApplied(flash.changeId) ? flash.eventId : null;
  const respond = useCallback(
    (event: CalEvent, response: "yes" | "may" | "no") => stage(respondChange(event, response)),
    [stage],
  );
  const loadSchedules = useCallback(
    (emails: readonly string[], start: Date, end: Date) =>
      graphToken
        ? fetchSchedules(graphToken, () => refreshGraphToken(false), emails, start, end)
        : Promise.reject(new Error("Not signed in")),
    [graphToken, refreshGraphToken],
  );
  const loadEvents = useCallback(
    (start: Date, end: Date) =>
      graphToken
        ? fetchCalendarView(graphToken, () => refreshGraphToken(false), start, end).then((r) => r.events)
        : Promise.reject(new Error("Not signed in")),
    [graphToken, refreshGraphToken],
  );
  const loadAttachments = useCallback(
    (eventId: string) =>
      graphToken
        ? fetchAttachments(graphToken, () => refreshGraphToken(false), eventId)
        : Promise.reject(new Error("Not signed in")),
    [graphToken, refreshGraphToken],
  );
  const loadRepeat = useCallback(
    (seriesMasterId: string) =>
      graphToken
        ? fetchRecurrenceLabel(graphToken, () => refreshGraphToken(false), seriesMasterId)
        : Promise.reject(new Error("Not signed in")),
    [graphToken, refreshGraphToken],
  );
  const loadThread = useCallback(
    (event: CalEvent) =>
      graphToken
        ? findMeetingThread(graphToken, () => refreshGraphToken(false), event, self.emails)
        : Promise.reject(new Error("Not signed in")),
    [graphToken, refreshGraphToken, self.emails],
  );
  // A new time was picked in the finder: the meeting screen follows the meeting to its new day.
  const pickNewTime = useCallback(
    (change: CalendarChange, eventId: string, start: Date) => {
      stage(change);
      if (change.kind === "reschedule") setDay(dayKeyOf(start));
      setScreen({ kind: "meeting", eventId });
    },
    [stage],
  );
  // E8: a meeting opened in Outlook isn't in the calendar until the user sends
  // it, so once one has been opened the calendar is read again whenever the
  // pane is come back to (at most every 15 s).
  const [awaitingInvite, setAwaitingInvite] = useState(false);
  const lastReturnRef = useRef(0);
  useEffect(() => {
    if (!awaitingInvite) return;
    const onReturn = () => {
      if (document.visibilityState !== "visible" || Date.now() - lastReturnRef.current < RETURN_REFRESH_MS) return;
      lastReturnRef.current = Date.now();
      void refresh();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [awaitingInvite, refresh]);
  // The invite's day is shown on return, as in the mockup, so a draft saved in Outlook appears there.
  const inviteOpened = useCallback(
    ({ teamsAdded, start, web }: { readonly teamsAdded: boolean | null; readonly start: Date; readonly web: boolean }) => {
      setDay(dayKeyOf(start));
      setView("day");
      setScreen({ kind: "agenda" });
      setAwaitingInvite(true);
      const where = web ? "Outlook on the web" : "Outlook";
      let next = "send it, or save it to keep it as a draft";
      if (teamsAdded === false) next = "turn on Teams meeting in it, then send it";
      else if (web) next = "check the details and send it";
      notify({ kind: "info", message: `Invite opened in ${where} · ${next}` });
    },
    [notify],
  );
  const draftOpened = useCallback(() => setAwaitingInvite(true), []);
  // A repeating meeting sent from the pane: it shows on its day straight away, with Undo until it goes out.
  const sendSeries = useCallback(
    (change: CalendarChange, start: Date) => {
      stage(change);
      setDay(dayKeyOf(start));
      setView("day");
      setScreen({ kind: "agenda" });
    },
    [stage],
  );
  const inviteFailed = useCallback((message: string) => notify({ kind: "error", message }), [notify]);
  const toast = <CalendarToast toast={changes.toast} />;

  const moving = screen.kind === "reschedule" ? events.find((e) => e.id === screen.eventId) : undefined;
  const selected = screen.kind === "meeting" ? events.find((e) => e.id === screen.eventId) : undefined;
  const onSchedule = screen.kind !== "new" && !moving && !selected;

  /** Ctrl/⌘+Z: the toast's Undo, while it offers one. */
  const undoKey = (e: KeyboardEvent) => {
    const onUndo = changes.toast?.kind === "undo" ? changes.toast.onUndo : undefined;
    if (!onUndo) return;
    e.preventDefault();
    onUndo();
  };
  /** ← →: the previous or next day, or week in the week view. */
  const stepKey = (e: KeyboardEvent, key: string) => {
    if (e.shiftKey) return;
    e.preventDefault();
    setDay(addDays(day, (key === "arrowleft" ? -1 : 1) * (view === "day" ? 1 : 7)));
  };
  /** T, D, W, L and N; false when `key` isn't one of them here. */
  const letterKey = (key: string): boolean => {
    if (key === "t") goToday();
    else if (key === "d") setView("day");
    else if (key === "w") setView("week");
    else if (key === "l" && view === "day") setMode((m) => (m === "timeline" ? "list" : "timeline"));
    else if (key === "n") showNewMeeting();
    else return false;
    return true;
  };
  // The mockup's keys, each doing what its button does on the screen shown. The
  // meeting screen pages with ← → itself, New Meeting handles its own Esc, and
  // the day view its F (Focus Hour).
  useCalendarKeys((e) => {
    if (!graphToken) return;
    const key = e.key.toLowerCase();
    if (key === "z" && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
      undoKey(e);
      return;
    }
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (key === "escape") {
      if (moving) setScreen({ kind: "meeting", eventId: moving.id });
      else if (selected) showAgenda();
      return;
    }
    if (!onSchedule) return;
    if (key === "arrowleft" || key === "arrowright") stepKey(e, key);
    else if (!e.repeat && letterKey(key)) e.preventDefault();
  });

  if (!graphToken) {
    return (
      <div className={styles.module}>
        <ModuleHeader
          title="Calendar"
          subtitle="Connect Microsoft 365 to view calendar"
          icon={CalendarIcon}
          accent="calendar"
          onBack={onBack}
        />
        <div className={styles.empty}>Sign in to access your calendar.</div>
      </div>
    );
  }

  if (screen.kind === "new") {
    return (
      <NewMeetingView
        preset={screen.preset}
        startStep={screen.step}
        now={now}
        events={events}
        workingHours={workingHours}
        token={graphToken}
        self={self}
        loadSchedules={loadSchedules}
        toast={toast}
        onClose={showAgenda}
        onOpened={inviteOpened}
        onError={inviteFailed}
        onSend={sendSeries}
      />
    );
  }

  if (moving) {
    return (
      <div className={styles.module} {...calendarScreenProps}>
        <ModuleHeader
          title="Reschedule"
          subtitle={`Pick a new time for ${displaySubject(moving)}`}
          icon={CalendarIcon}
          accent="calendar"
          onBack={() => setScreen({ kind: "meeting", eventId: moving.id })}
          backLabel="Back to meeting"
        />
        <div className={styles.body}>
          <RescheduleView
            event={moving}
            now={now}
            workingHours={workingHours}
            token={graphToken}
            selfEmails={self.emails}
            loadSchedules={loadSchedules}
            onPick={(change, start) => pickNewTime(change, moving.id, start)}
          />
        </div>
        {toast}
      </div>
    );
  }

  if (selected) {
    const clashWith = clashPartner(findClashes(segs, acks), selected.id);
    return (
      <div className={styles.module} {...calendarScreenProps}>
        <ModuleHeader
          title="Meeting"
          subtitle={`Back to ${dayName(day, today)}`}
          icon={CalendarIcon}
          accent="calendar"
          onBack={showAgenda}
          backLabel="Back to schedule"
        />
        <div className={styles.body}>
          <MeetingDetail
            event={selected}
            dayEvents={dayEvents}
            clashWith={clashWith}
            token={graphToken}
            self={self}
            now={now}
            writable={writable}
            loadSchedules={loadSchedules}
            onStage={stage}
            onOpenEvent={openEvent}
            onSeeOptions={() => seeClashOptions(selected.id)}
            onReschedule={() => setScreen({ kind: "reschedule", eventId: selected.id })}
            onOpenedInOutlook={draftOpened}
            loadThread={loadThread}
          />
        </div>
        {toast}
      </div>
    );
  }

  const subtitle = view === "week" ? `${weekOfLabel(mon)} · your load and replies` : daySubtitle(segs, loading, error, day, today);

  return (
    <div className={styles.module} {...calendarScreenProps}>
      <ModuleHeader
        title="Calendar"
        subtitle={subtitle}
        icon={CalendarIcon}
        accent="calendar"
        onBack={onBack}
        actions={
          <button
            type="button"
            className={styles.newLink}
            onClick={showNewMeeting}
            aria-label="New meeting"
            title="New meeting"
          >
            <PlusIcon />
            New
          </button>
        }
      />

      <div className={styles.toolbar}>
        {view === "day" ? (
          <>
            <button
              type="button"
              className={`${styles.icoBtn} ${styles.icoBtnSm}`}
              onClick={() => setDay(addDays(day, -1))}
              aria-label="Previous day"
            >
              <ChevronLeftSmIcon />
            </button>
            <span className={styles.toolbarLabel}>{dayName(day, today)}</span>
            <button
              type="button"
              className={`${styles.icoBtn} ${styles.icoBtnSm}`}
              onClick={() => setDay(addDays(day, 1))}
              aria-label="Next day"
            >
              <ChevronRightSmIcon />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`${styles.icoBtn} ${styles.icoBtnSm}`}
              onClick={() => setDay(addDays(day, -7))}
              aria-label="Previous week"
            >
              <ChevronLeftSmIcon />
            </button>
            <span className={styles.toolbarLabel}>{weekLabel(mon)}</span>
            <button
              type="button"
              className={`${styles.icoBtn} ${styles.icoBtnSm}`}
              onClick={() => setDay(addDays(day, 7))}
              aria-label="Next week"
            >
              <ChevronRightSmIcon />
            </button>
          </>
        )}
        <button
          type="button"
          className={styles.todayBtn}
          onClick={goToday}
          disabled={view === "day" && day === today}
        >
          Today
        </button>
        <span className={styles.seg}>
          <button
            type="button"
            className={view === "day" ? styles.segOn : ""}
            aria-pressed={view === "day"}
            onClick={() => setView("day")}
          >
            Day
          </button>
          <button
            type="button"
            className={view === "week" ? styles.segOn : ""}
            aria-pressed={view === "week"}
            onClick={() => setView("week")}
          >
            Week
          </button>
        </span>
      </div>

      <div className={styles.body}>
        {view === "week" ? (
          <WeekView
            mon={mon}
            today={today}
            selectedDay={day}
            nowMin={minutesOfDay(now)}
            events={events}
            workingHours={workingHours}
            loading={loading}
            error={error}
            onRetry={reload}
            acks={acks}
            onOpenDay={openDay}
            onOpenEvent={openEvent}
            writable={writable}
            onRespond={respond}
          />
        ) : (
          <DayView
            day={day}
            today={today}
            now={now}
            nowMin={minutesOfDay(now)}
            events={events}
            workingHours={workingHours}
            loading={loading}
            error={error}
            truncated={truncated}
            onRetry={reload}
            mode={mode}
            onModeChange={setMode}
            acks={acks}
            onLeaveIt={leaveIt}
            onOpenEvent={openEvent}
            onBook={bookAt}
            onFind={findATime}
            resolveFor={resolveFor}
            onResolveOpened={resolveOpened}
            writable={writable}
            selfEmails={self.emails}
            flashId={flashId}
            onStage={stage}
            onNotify={notify}
            onNavigate={openDay}
            loadSchedules={loadSchedules}
            loadEvents={loadEvents}
            loadAttachments={loadAttachments}
            loadRepeat={loadRepeat}
            loadThread={loadThread}
          />
        )}
      </div>
      {toast}
    </div>
  );
}
